import {
    CurrencyType,
    getOptions,
    InputField,
    Invoice,
    PaymentPlanIntervalType,
    ProviderPaymentPlan,
    SelectField,
    SupportCreatePaymentPlanPayload,
    ToastContext,
} from '@enter/core';
import { mdiInformation } from '@mdi/js';
import { Formik, FormikHelpers as FormikActions, FormikProps } from 'formik';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { DialogContext } from 'services';
import { GuarantorService } from 'stores/guarantor.service';
import * as Yup from 'yup';
import * as S from './create-plan-dialog.styles';

type ICreatePlanDialogProps = {
    onClose: () => void;
    onSubmit: (plan: ProviderPaymentPlan) => void;
    invoiceId?: string;
    invoices?: Invoice[];
};

const schema = Yup.object().shape({});

const initial: SupportCreatePaymentPlanPayload = {
    amountPerCycle: {
        amount: undefined,
        currency: CurrencyType.USD,
    },
    intervalCount: 1,
    intervalSpan: PaymentPlanIntervalType.DAY,
};

function getDisplayInvoiceName(invoice: Invoice): string {
    const typeStatus = [invoice.invoiceType, invoice.invoiceStatus];
    const invoiceAmount = invoice.amount ? `Amount: ${invoice.amount.currency} ${invoice.amount.amount}` : undefined;

    return _.compact([...typeStatus, invoiceAmount, invoice.invoiceDate]).join(', ');
}

export const CreatePlanDialog: React.FunctionComponent<ICreatePlanDialogProps> = inject('rootStore')(
    observer(
        (props: ICreatePlanDialogProps): React.ReactElement => {
            const { onClose, onSubmit, invoiceId, invoices } = props;
            const context = useContext(ToastContext);
            const dialogContext = useContext(DialogContext);

            const handleCancel = (): void => {
                onClose();
            };
            const handleSubmit = async (
                values: SupportCreatePaymentPlanPayload,
                { setSubmitting }: FormikActions<any>,
            ): Promise<void> => {
                setSubmitting(true);

                try {
                    const plan = await GuarantorService.createPaymentPlan(values);
                    if (plan) {
                        context.notify && context.notify('Plan created');
                        onSubmit(plan);
                    } else {
                        context.notify && context.notify('Payment plans are not available');
                        onClose();
                    }
                } catch (err) {
                    dialogContext.showError(err);
                }

                setSubmitting(false);
            };

            const invoiceOptions = _.map(invoices, invoice => ({
                value: invoice.id,
                label: getDisplayInvoiceName(invoice),
            }));

            return (
                <Formik
                    initialValues={{ ...initial, invoiceId: invoiceId || _.first(invoices)?.id }}
                    validationSchema={schema}
                    onSubmit={handleSubmit}>
                    {(formik) => {
                        const amount = formik.values.amountPerCycle?.amount;
                        return (
                            <S.DialogBody>
                                <S.InputGroup>
                                    <S.Label>Amount</S.Label>
                                    <S.FormField name="amountPerCycle.amount" component={InputField} type="number" />

                                    <S.Label>Payment interval</S.Label>
                                    <S.InputRow>
                                        <S.InputCol>
                                            <S.FormField name="intervalCount" component={InputField} type="number" />
                                        </S.InputCol>

                                        <S.InputCol>
                                            <S.FormField
                                                name="intervalSpan"
                                                component={SelectField}
                                                options={getOptions(PaymentPlanIntervalType)}
                                                required={true}
                                            />
                                        </S.InputCol>
                                    </S.InputRow>

                                    {!invoiceId && (
                                        <>
                                            <S.Label>Select invoice</S.Label>

                                            <S.FormField
                                                name="invoiceId"
                                                component={SelectField}
                                                options={invoiceOptions}
                                                required={true}
                                            />
                                        </>
                                    )}
                                </S.InputGroup>
                                {!!amount && parseFloat(amount) > 0 && (
                                    <S.InfoWrapper>
                                        <S.WrapperIcon size={1} path={mdiInformation} />
                                        <S.InfoLabel>
                                            The user would get billed ${formik.values.amountPerCycle?.amount} every{' '}
                                            {formik.values.intervalCount} {formik.values.intervalSpan} until the invoice
                                            is paid off.
                                        </S.InfoLabel>
                                    </S.InfoWrapper>
                                )}
                                <S.ButtonGroup
                                    changed={true}
                                    useNotify={false}
                                    onCancel={handleCancel}
                                    onSave={formik.submitForm}
                                    saveLabel="Create Plan"
                                    saveNotice="Plan created"
                                />
                            </S.DialogBody>
                        );
                    }}
                </Formik>
            );
        },
    ),
);
